import { Pencil, Trash2 } from "lucide-react";

import { Button } from "@/common/ui/button";
import { TableCell, TableRow } from "@/common/ui/table";

import { getEnvironmentsForOpsBuild } from "./environments";
import type { FeatureFlag } from "./feature-flags-types";

interface FlagRowProps {
  flag: FeatureFlag;
  onToggleEnv: (key: string, environment: string, enabled: boolean) => void;
  onEdit: (key: string) => void;
  onDelete: (key: string) => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function envEnabled(flag: FeatureFlag, environment: string): boolean {
  const override = flag.enabledByEnvironment?.[environment];
  if (typeof override === "boolean") return override;
  return flag.enabled;
}

function formatVariation(flag: FeatureFlag): string {
  const value = JSON.stringify(flag.defaultVariationValue);
  return `${flag.defaultVariationKey} → ${value}`;
}

export function FlagRow({ flag, onToggleEnv, onEdit, onDelete }: FlagRowProps): React.ReactElement {
  return (
    <TableRow data-testid={`flag-row-${flag.flagKey}`}>
      <TableCell className="font-mono text-sm">{flag.flagKey}</TableCell>
      <TableCell className="max-w-[260px] truncate text-sm text-muted-foreground">
        {flag.description || "—"}
      </TableCell>
      <TableCell
        className="font-mono text-xs text-muted-foreground"
        data-testid={`flag-default-${flag.flagKey}`}
        title="Default variation returned when no rule matches"
      >
        {formatVariation(flag)}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {/*
            Pill set depends on the ops console's own build env:
            - Prod ops console → real consumer envs (dev/staging/integration/prod)
            - Non-prod ops console → its own {env}-test sandbox only
            See ./environments.ts for the rationale.
          */}
          {getEnvironmentsForOpsBuild().map((env) => {
            const enabled = envEnabled(flag, env);
            return (
              <Button
                key={env}
                data-testid={`flag-env-toggle-${flag.flagKey}-${env}`}
                variant={enabled ? "default" : "outline"}
                size="sm"
                className="h-7 px-2.5 text-[11px] uppercase tracking-wide"
                onClick={() => onToggleEnv(flag.flagKey, env, !enabled)}
                aria-pressed={enabled}
                aria-label={`${enabled ? "Disable" : "Enable"} ${flag.flagKey} in ${env}`}
                title={`${env}: ${enabled ? "enabled" : "disabled"} — click to ${enabled ? "disable" : "enable"}`}
              >
                {env}
              </Button>
            );
          })}
        </div>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{formatDate(flag.updatedAt)}</TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button
            data-testid={`edit-flag-${flag.flagKey}`}
            variant="ghost"
            size="sm"
            onClick={() => onEdit(flag.flagKey)}
            title="Edit description and default variation"
            aria-label={`Edit ${flag.flagKey}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            data-testid={`delete-flag-${flag.flagKey}`}
            variant="ghost"
            size="sm"
            onClick={() => onDelete(flag.flagKey)}
            title="Delete flag"
            aria-label={`Delete ${flag.flagKey}`}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
